import { FormEvent, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { ArrowLeft, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPatient, createMedicalRecord, getPatientMedicalFilePro, searchPatients } from "../../../api/professionalPatient";
import {
  getBeninArrondissements,
  getBeninCommunes,
  getBeninDepartements,
  getProfessionsMetiers,
  type BeninArrondissement,
  type BeninCommune,
  type BeninDepartement,
  type ProfessionMetier,
} from "../../../api/publicBenin";
import { useAuth } from "../../../store/authEtablissementStore";
import { RequiredMark } from "../../../components/RequiredMark";
import PatientAccessOtpModal from "../../../components/PatientAccessOtpModal";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const GROUPES_SANGUINS = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];
const ELECTROPHORESES = ["AA", "AS", "SS", "AC", "SC"];

function toSlug(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

type Step = 1 | 2 | 3;

const BJ_DIAL = "+229";

type Mode = "nouveau" | "existant";

export default function AddPatientEtablissement() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("nouveau");
  const [prefillNip, setPrefillNip] = useState("");

  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0B1F16]">Gestion des patients</h1>
      <div className="mt-4 text-sm" style={{ color: "#00553E" }}>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2">
          <ArrowLeft size={16} /> Retour
        </button>
      </div>
      <div className="mt-2">
        <div className="text-2xl font-semibold text-[#0B1F16]">Ajouter un patient</div>
      </div>

      <div className="mt-5 inline-flex rounded-[10px] border bg-white p-1" style={{ borderColor: "#E5E7EB" }}>
        {([
          { id: "nouveau" as const, label: "Nouveau patient" },
          { id: "existant" as const, label: "Patient existant" },
        ]).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setMode(t.id)}
            className="h-10 px-4 rounded-[8px] text-sm font-semibold transition-colors"
            style={{
              background: mode === t.id ? "#00553E" : "transparent",
              color: mode === t.id ? "#fff" : "#0B1F16",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {mode === "nouveau" ? (
          <NewPatientWizard initialNip={prefillNip} />
        ) : (
          <ExistingPatientFlow
            onPatientNotFound={(nip) => {
              setPrefillNip(nip);
              setMode("nouveau");
            }}
          />
        )}
      </div>
    </div>
  );
}

function NewPatientWizard({ initialNip = "" }: { initialNip?: string }) {
  const navigate = useNavigate();
  const { establishment } = useAuth();
  const etablissementId = establishment?.id?.trim();
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingDossier, setPendingDossier] = useState<{
    patientNip: string;
    payload: Record<string, unknown>;
  } | null>(null);

  const [nip, setNip] = useState(initialNip);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [birth, setBirth] = useState("");
  const [numero, setNumero] = useState("");
  const [email, setEmail] = useState("");
  const [job, setJob] = useState("");
  const [jobAutre, setJobAutre] = useState("");
  const [departementId, setDepartementId] = useState("");
  const [communeNom, setCommuneNom] = useState("");
  const [arrondissementNom, setArrondissementNom] = useState("");
  const [quartierLibre, setQuartierLibre] = useState("");
  const [departements, setDepartements] = useState<BeninDepartement[]>([]);
  const [communes, setCommunes] = useState<BeninCommune[]>([]);
  const [arrondissements, setArrondissements] = useState<BeninArrondissement[]>([]);
  const [metiers, setMetiers] = useState<ProfessionMetier[]>([]);
  const [loadingMetiers, setLoadingMetiers] = useState(true);
  const [loadingDepartements, setLoadingDepartements] = useState(true);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loadingArrondissements, setLoadingArrondissements] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);

  const [taille, setTaille] = useState("");
  const [poids, setPoids] = useState("");
  const [gs, setGs] = useState("");
  const [electro, setElectro] = useState("");
  const [allergies, setAllergies] = useState("");
  const [intolerances, setIntolerances] = useState("");
  const [vaccins, setVaccins] = useState<string[]>(["Polio", "Fievre jaune", "COVID-19"]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [coverage, setCoverage] = useState<"oui" | "non" | "verification">("verification");
  const [soins, setSoins] = useState<string[]>(["consultations", "maternité", "chirurgie"]);
  const [soinModalOpen, setSoinModalOpen] = useState(false);
  const [soinDraft, setSoinDraft] = useState("");
  const soinInputRef = useRef<HTMLInputElement | null>(null);

  const phonePartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const nipPartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const tailleRef = useRef<HTMLInputElement | null>(null);
  const poidsRef = useRef<HTMLInputElement | null>(null);

  const applyDigitsWithCaret = (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string) => void,
    ref: React.RefObject<HTMLInputElement | null>,
    maxLength: number
  ) => {
    const input = event.target;
    const selection = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = (input.value.slice(0, selection).match(/\d/g) || []).length;
    const only = input.value.replace(/\D/g, "").slice(0, maxLength);
    setter(only);
    requestAnimationFrame(() => {
      if (ref.current) {
        const position = Math.min(digitsBeforeCaret, only.length);
        try {
          ref.current.focus();
          ref.current.setSelectionRange(position, position);
        } catch {
          /* ignore */
        }
      }
    });
  };

  const emailValid = useMemo(() => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email.trim()), [email]);
  const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
  const nomValid = useMemo(() => nom.trim().length > 0 && nameRegex.test(nom.trim()), [nom]);
  const prenomValid = useMemo(() => prenom.trim().length > 0 && nameRegex.test(prenom.trim()), [prenom]);
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const birthValid = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birth)) return false;
    const d = new Date(birth);
    if (Number.isNaN(d.getTime())) return false;
    return birth >= "1900-01-01" && birth <= todayIso;
  }, [birth, todayIso]);
  const phoneValid = useMemo(() => /^\d{10}$/.test(numero), [numero]);
  const phoneParts = useMemo(
    () => [numero.slice(0, 2), numero.slice(2, 4), numero.slice(4, 6), numero.slice(6, 8), numero.slice(8, 10)],
    [numero],
  );
  const nipValid = useMemo(() => /^\d{10}$/.test(nip), [nip]);
  const nipParts = useMemo(
    () => [nip.slice(0, 2), nip.slice(2, 4), nip.slice(4, 6), nip.slice(6, 8), nip.slice(8, 10)],
    [nip]
  );
  const departementNom = useMemo(
    () => departements.find((d) => d.id === departementId)?.nom ?? "",
    [departements, departementId]
  );
  const adresse = useMemo(
    () =>
      [departementNom, communeNom, arrondissementNom, quartierLibre]
        .map((s) => s.trim())
        .filter(Boolean)
        .join("-"),
    [departementNom, communeNom, arrondissementNom, quartierLibre]
  );
  const adresseSlug = useMemo(
    () =>
      [departementNom, communeNom, arrondissementNom, quartierLibre]
        .map((s) => toSlug(s))
        .filter(Boolean)
        .join("-"),
    [departementNom, communeNom, arrondissementNom, quartierLibre]
  );

  useEffect(() => {
    let mounted = true;
    getProfessionsMetiers().then((result) => {
      if (!mounted) return;
      setLoadingMetiers(false);
      if (result.ok) setMetiers(result.data);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoadingDepartements(true);
    setAddressError(null);
    getBeninDepartements()
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setDepartements([]);
          setAddressError(result.message || "Impossible de charger les départements.");
          return;
        }
        setDepartements(result.data);
      })
      .finally(() => { if (mounted) setLoadingDepartements(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!departementId) {
      setCommunes([]);
      setCommuneNom("");
      setArrondissements([]);
      setArrondissementNom("");
      return () => { mounted = false; };
    }
    setLoadingCommunes(true);
    setAddressError(null);
    setCommuneNom("");
    setArrondissements([]);
    setArrondissementNom("");
    getBeninCommunes(departementId)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) { setCommunes([]); setAddressError(result.message || "Impossible de charger les communes."); return; }
        setCommunes(result.data);
      })
      .finally(() => { if (mounted) setLoadingCommunes(false); });
    return () => { mounted = false; };
  }, [departementId]);

  useEffect(() => {
    let mounted = true;
    if (!departementId || !communeNom) {
      setArrondissements([]);
      setArrondissementNom("");
      return () => { mounted = false; };
    }
    setLoadingArrondissements(true);
    setAddressError(null);
    setArrondissementNom("");
    getBeninArrondissements(departementId, communeNom)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) { setArrondissements([]); setAddressError(result.message || "Impossible de charger les arrondissements."); return; }
        setArrondissements(result.data);
      })
      .finally(() => { if (mounted) setLoadingArrondissements(false); });
    return () => { mounted = false; };
  }, [departementId, communeNom]);

  const updateNipPart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...nipParts];
    parts[index] = digits;
    setNip(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) requestAnimationFrame(() => nipPartRefs.current[index + 1]?.focus());
  };

  const handleNipPartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = nipPartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try { prev.setSelectionRange(prev.value.length, prev.value.length); } catch { /* ignore */ }
      });
    }
  };

  const updatePhonePart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...phoneParts];
    parts[index] = digits;
    setNumero(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) requestAnimationFrame(() => phonePartRefs.current[index + 1]?.focus());
  };

  const handlePhonePartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = phonePartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try { prev.setSelectionRange(prev.value.length, prev.value.length); } catch { /* ignore */ }
      });
    }
  };

  const handlePhonePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNumero(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => phonePartRefs.current[nextIndex]?.focus());
  };

  const handleNipPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNip(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => nipPartRefs.current[nextIndex]?.focus());
  };

  const canProceed = useMemo(() => {
    if (step === 1) {
      return (
        nomValid && prenomValid && nipValid && emailValid && birthValid &&
        phoneValid && departementId !== "" && communeNom !== "" && arrondissementNom !== ""
      );
    }
    return true;
  }, [step, nomValid, prenomValid, nipValid, emailValid, birthValid, phoneValid, departementId, communeNom, arrondissementNom]);

  const onNext = async () => {
    if (step < 3) {
      setStep((prev) => (prev + 1) as Step);
      return;
    }
    if (!etablissementId) {
      setSubmitError("Votre session ne contient pas d'identifiant établissement. Reconnectez-vous.");
      return;
    }
    if (!birthValid) {
      setSubmitError("Date de naissance invalide.");
      return;
    }
    const dateNaissance = `${birth}T00:00:00.000Z`;
    setSubmitting(true);
    setSubmitError(null);
    const poidsN = poids ? Number(poids) : undefined;
    const tailleN = taille ? Number(taille) : undefined;
    const professionValue = job === "autre" ? jobAutre.trim() : job.trim();

    const patientPayload: Record<string, unknown> = {
      nip: nip.trim(),
      numeroDossier: `DOS-${nip.trim()}`,
      nom: nom.trim().toUpperCase(),
      prenom: prenom.trim(),
      dateNaissance,
      email: email.trim(),
      telephone: `${BJ_DIAL}${numero.trim()}`,
      adresse,
      adresseSlug,
      poids: poidsN,
      taille: tailleN,
      groupeSanguin: gs || undefined,
      electrophorese: electro || undefined,
      profession: professionValue || undefined,
      couvertureAMO: coverage,
      typeAssurance: "AMO",
      createur: etablissementId,
    };

    const created = await createPatient(patientPayload);
    if (!created.ok) {
      const msg =
        created.status === 403
          ? "Opération réservée aux établissements de santé ou administrateurs."
          : created.message;
      setSubmitError(msg);
      setSubmitting(false);
      return;
    }
    const data = created.data && typeof created.data === "object" ? (created.data as Record<string, unknown>) : {};
    const patientNip = String(data.nip ?? nip.trim());

    const allergiesList = allergies.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    const intolerancesList = intolerances.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    const vaccinationsPayload = vaccins.map((v) => ({
      type: v.toLowerCase().replace(/\s+/g, "_"),
      date: new Date().toISOString(),
    }));

    const dossierPayload: Record<string, unknown> = {
      patientNip,
      informationsPersonnelles: {
        nom: nom.trim().toUpperCase(),
        prenom: prenom.trim(),
        dateNaissance,
        adresse,
        adresseSlug,
        email: email.trim(),
        telephone: `${BJ_DIAL}${numero.trim()}`,
        profession: professionValue || undefined,
      },
      informationsMedicales: {
        poids: poidsN,
        taille: tailleN,
        groupeSanguin: gs || undefined,
        electrophorese: electro || undefined,
        allergies: allergiesList,
        intolerances: intolerancesList,
        vaccinations: vaccinationsPayload,
        antecedents: [] as string[],
      },
      createur: etablissementId,
    };

    const dossierRes = await createMedicalRecord(dossierPayload);
    if (!dossierRes.ok) {
      if (dossierRes.errorCode === "ACCES_DOSSIER_REQUIS") {
        setPendingDossier({ patientNip, payload: dossierPayload });
        setSubmitting(false);
        return;
      }
      setSubmitError(`Patient créé, mais dossier médical non enregistré : ${dossierRes.message}`);
    }
    setSubmitting(false);
    navigate("/etablissement/dashboard/patients");
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onNext();
  };

  const handleDossierAccessGranted = async () => {
    if (!pendingDossier) return;
    setSubmitting(true);
    const retry = await createMedicalRecord(pendingDossier.payload);
    setSubmitting(false);
    if (!retry.ok) {
      setSubmitError(`Dossier médical non enregistré après validation de l'accès : ${retry.message}`);
    }
    setPendingDossier(null);
    navigate("/etablissement/dashboard/patients");
  };

  return (
    <div>
      {submitError && (
        <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 max-w-3xl">
          {submitError}
        </div>
      )}

      <form className="mt-6 space-y-6" onSubmit={onSubmit}>
        {step === 1 && (
          <div className="bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-2xl font-bold text-[#0B1F16] mb-4">Informations personnelles</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="font-semibold mb-1">Nom <RequiredMark /></div>
                <input
                  value={nom}
                  onChange={(e) => setNom(e.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, ""))}
                  className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3"
                  style={{ borderColor: nomValid || nom === "" ? "#E5E7EB" : "#F87171" }}
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Prenoms <RequiredMark /></div>
                <input
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, ""))}
                  className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3"
                  style={{ borderColor: prenomValid || prenom === "" ? "#E5E7EB" : "#F87171" }}
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Date de naissance <RequiredMark /></div>
                <input
                  type="date"
                  value={birth}
                  onChange={(e) => setBirth(e.target.value)}
                  max={todayIso}
                  min="1900-01-01"
                  className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3"
                  style={{ borderColor: birthValid || birth === "" ? "#E5E7EB" : "#F87171" }}
                />
              </div>
              <div className="md:col-span-2">
                <div className="font-semibold mb-1">Numéro <RequiredMark /></div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex h-10 shrink-0 items-center gap-2 rounded-[10px] border bg-[#F3F4F4] px-3" style={{ borderColor: "#E5E7EB" }}>
                    <span className="text-lg leading-none" role="img" aria-label="Bénin">🇧🇯</span>
                    <span className="text-sm font-semibold text-[#0B1F16] tabular-nums">{BJ_DIAL}</span>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {phoneParts.map((part, index) => (
                      <div key={`phone-part-${index}`} className="flex items-center gap-2">
                        <input
                          value={part}
                          ref={(el) => { phonePartRefs.current[index] = el; }}
                          onChange={(e) => updatePhonePart(index, e.target.value)}
                          onKeyDown={(e) => handlePhonePartKeyDown(index, e)}
                          onPaste={handlePhonePaste}
                          inputMode="numeric"
                          maxLength={2}
                          placeholder="00"
                          className="h-10 w-12 rounded-[10px] border px-1 text-center bg-[#F3F4F4]"
                          style={{ borderColor: phoneValid || numero === "" ? "#E5E7EB" : "#F87171" }}
                        />
                        {index < 4 && <span className="font-semibold text-[#0B1F16]/70">-</span>}
                      </div>
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-xs text-[#0B1F16]/60">10 chiffres après {BJ_DIAL}.</p>
              </div>
              <div>
                <div className="font-semibold mb-1">Email <RequiredMark /></div>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value.replace(/\s/g, ""))}
                  type="email"
                  placeholder="exemple@domaine.com"
                  className="h-10 w-full rounded-[10px] border px-3 bg-[#F3F4F4]"
                  style={{ borderColor: emailValid || email === "" ? "#E5E7EB" : "#F87171" }}
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Profession</div>
                <select
                  value={job}
                  onChange={(e) => {
                    setJob(e.target.value);
                    if (e.target.value !== "autre") setJobAutre("");
                  }}
                  disabled={loadingMetiers}
                  className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 disabled:opacity-70"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <option value="">{loadingMetiers ? "Chargement..." : "Sélectionner une profession"}</option>
                  {metiers.map((m) => <option key={m.slug} value={m.slug}>{m.title}</option>)}
                </select>
                {job === "autre" && (
                  <input
                    value={jobAutre}
                    onChange={(e) => setJobAutre(e.target.value)}
                    placeholder="Précisez la profession"
                    className="mt-2 h-10 w-full rounded-[10px] border px-3 bg-[#F3F4F4]"
                    style={{ borderColor: "#E5E7EB" }}
                  />
                )}
              </div>
              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-semibold mb-1">Département <RequiredMark /></div>
                  <select
                    value={departementId}
                    onChange={(e) => setDepartementId(e.target.value)}
                    disabled={loadingDepartements}
                    className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 disabled:opacity-70"
                    style={{ borderColor: "#E5E7EB" }}
                  >
                    <option value="">{loadingDepartements ? "Chargement..." : "Sélectionner un département"}</option>
                    {departements.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
                  </select>
                </div>
                <div>
                  <div className="font-semibold mb-1">Commune <RequiredMark /></div>
                  <select
                    value={communeNom}
                    onChange={(e) => setCommuneNom(e.target.value)}
                    disabled={!departementId || loadingCommunes}
                    className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 disabled:opacity-70"
                    style={{ borderColor: "#E5E7EB" }}
                  >
                    <option value="">{!departementId ? "Choisir d'abord un département" : loadingCommunes ? "Chargement..." : "Sélectionner une commune"}</option>
                    {communes.map((c) => <option key={`${c.id ?? c.nom}-${c.nom}`} value={c.nom}>{c.nom}</option>)}
                  </select>
                </div>
                <div>
                  <div className="font-semibold mb-1">Arrondissement <RequiredMark /></div>
                  <select
                    value={arrondissementNom}
                    onChange={(e) => setArrondissementNom(e.target.value)}
                    disabled={!communeNom || loadingArrondissements}
                    className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 disabled:opacity-70"
                    style={{ borderColor: "#E5E7EB" }}
                  >
                    <option value="">{!communeNom ? "Choisir d'abord une commune" : loadingArrondissements ? "Chargement..." : "Sélectionner un arrondissement"}</option>
                    {arrondissements.map((a) => <option key={`${a.id ?? a.nom}-${a.nom}`} value={a.nom}>{a.nom}</option>)}
                  </select>
                </div>
                <div>
                  <div className="font-semibold mb-1">Quartier</div>
                  <input
                    value={quartierLibre}
                    onChange={(e) => setQuartierLibre(e.target.value)}
                    className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3"
                    style={{ borderColor: "#E5E7EB" }}
                    placeholder="Ex: Houeyiho"
                  />
                </div>
                <div className="md:col-span-2">
                  <div className="font-semibold mb-1">Adresse (concaténée)</div>
                  <input
                    value={adresse}
                    readOnly
                    className="h-10 w-full rounded-[10px] border bg-[#EEF1F1] px-3 text-[#0B1F16]"
                    style={{ borderColor: "#E5E7EB" }}
                    placeholder="Departement-Commune-Arrondissement-Quartier"
                  />
                  {addressError && <p className="mt-1 text-xs text-red-600">{addressError}</p>}
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">NIP <RequiredMark /></div>
                <div className="flex items-center gap-2">
                  {nipParts.map((part, index) => (
                    <div key={`nip-part-${index}`} className="flex items-center gap-2">
                      <input
                        value={part}
                        ref={(el) => { nipPartRefs.current[index] = el; }}
                        onChange={(e) => updateNipPart(index, e.target.value)}
                        onKeyDown={(e) => handleNipPartKeyDown(index, e)}
                        onPaste={handleNipPaste}
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={2}
                        placeholder="00"
                        className="h-10 w-12 text-center rounded-[10px] border px-1 bg-[#F3F4F4]"
                        style={{ borderColor: nipValid || nip === "" ? "#E5E7EB" : "#F87171" }}
                      />
                      {index < 4 && <span className="text-[#0B1F16]/70 font-semibold">-</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-2xl font-bold text-[#0B1F16] mb-4">Informations medicales</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-semibold mb-1">Taille</div>
                <div className="relative">
                  <Input
                    value={taille}
                    onChange={(e) => applyDigitsWithCaret(e, setTaille, tailleRef, 4)}
                    ref={tailleRef}
                    inputMode="numeric"
                    pattern="\d*"
                    className="pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">cm</span>
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">Groupe sanguin</div>
                <Select value={gs || undefined} onValueChange={setGs}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUPES_SANGUINS.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="font-semibold mb-1">Poids</div>
                <div className="relative">
                  <Input
                    value={poids}
                    onChange={(e) => applyDigitsWithCaret(e, setPoids, poidsRef, 4)}
                    ref={poidsRef}
                    inputMode="numeric"
                    pattern="\d*"
                    className="pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kg</span>
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">Electrophorèse</div>
                <Select value={electro || undefined} onValueChange={setElectro}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {ELECTROPHORESES.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="font-semibold mb-1">Allergies</div>
                <Input value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Ex : pénicilline, arachide…" />
              </div>
              <div>
                <div className="font-semibold mb-1">Intolérances</div>
                <Input value={intolerances} onChange={(e) => setIntolerances(e.target.value)} placeholder="Ex : lactose, gluten…" />
              </div>
              <div className="md:col-span-2">
                <div className="font-semibold mb-2">Vaccinations</div>
                <div className="flex flex-wrap gap-2 relative">
                  {vaccins.map((v) => (
                    <span key={v} className="inline-flex items-center gap-2 px-3 h-9 rounded-[10px] border bg-white" style={{ borderColor: "#E5E7EB" }}>
                      {v}
                      <button type="button" onClick={() => setVaccins((prev) => prev.filter((x) => x !== v))}>✕</button>
                    </span>
                  ))}
                  <div className="relative">
                    <button type="button" className="inline-flex items-center px-3 h-9 rounded-[10px] border bg-white" style={{ borderColor: "#E5E7EB" }} onClick={() => setPickerOpen((o) => !o)}>
                      Ajouter
                    </button>
                    {pickerOpen && (
                      <div className="absolute z-10 mt-2 w-56 bg-white rounded-[10px] border p-3 space-y-2 shadow" style={{ borderColor: "#E5E7EB" }}>
                        {["Varicelle", "BCG", "DTP", "MMR", "Hépatite", "HPV", "Hib"].map((opt) => (
                          <label key={opt} className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={vaccins.includes(opt)} onChange={(e) => setVaccins((prev) => e.target.checked ? [...prev, opt] : prev.filter((x) => x !== opt))} />
                            {opt}
                          </label>
                        ))}
                        <div className="pt-2 flex justify-end">
                          <button className="h-9 px-3 rounded-[10px] font-semibold text-white" style={{ background: "#00553E" }} onClick={() => setPickerOpen(false)}>Ajouter</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-2xl font-bold text-[#0B1F16] mb-4">Assurance</div>
            <div>
              <div className="font-semibold mb-3">Couverture AMO</div>
              <div className="flex flex-wrap gap-3">
                {([
                  { value: "oui", label: "Assuré (AMO actif)", desc: "Patient couvert par l'AMO" },
                  { value: "non", label: "Non assuré", desc: "Pas de couverture AMO" },
                  { value: "verification", label: "À vérifier", desc: "Statut à confirmer" },
                ] as const).map(({ value, label, desc }) => (
                  <label
                    key={value}
                    className={`flex items-start gap-3 cursor-pointer rounded-[10px] border px-4 py-3 flex-1 min-w-[160px] transition-colors ${coverage === value ? "border-[#00553E] bg-[#00553E]/5" : "border-[#E5E7EB] bg-[#F3F4F4]"}`}
                  >
                    <input type="radio" name="coverage" value={value} checked={coverage === value} onChange={() => setCoverage(value)} className="mt-0.5 accent-[#00553E]" />
                    <span>
                      <span className="block text-sm font-semibold text-[#0B1F16]">{label}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="font-semibold mb-2">Soins inclus</div>
              <div className="flex flex-wrap gap-2">
                {soins.map((soin) => (
                  <span key={soin} className="inline-flex items-center gap-2 px-3 h-9 rounded-[10px] border bg-white" style={{ borderColor: "#E5E7EB" }}>
                    {soin}
                    <button type="button" onClick={() => setSoins((prev) => prev.filter((s) => s !== soin))}>✕</button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => { setSoinDraft(""); setSoinModalOpen(true); requestAnimationFrame(() => soinInputRef.current?.focus()); }}
                  className="h-9 px-3 rounded-[10px] border"
                  style={{ borderColor: "#00553E", color: "#00553E" }}
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-0 md:justify-between">
          <div className="flex items-center justify-center md:justify-start gap-4 md:gap-6">
            <div className={`h-1.5 w-16 md:w-24 rounded-full ${step >= 1 ? "bg-[#00553E]" : "bg-gray-300"}`} />
            <div className={`h-1.5 w-16 md:w-24 rounded-full ${step >= 2 ? "bg-[#00553E]" : "bg-gray-300"}`} />
            <div className={`h-1.5 w-16 md:w-24 rounded-full ${step >= 3 ? "bg-[#00553E]" : "bg-gray-300"}`} />
          </div>
          <button
            type="submit"
            disabled={!canProceed || submitting}
            className="h-11 px-5 rounded-[10px] font-semibold text-white disabled:opacity-60 w-full md:w-auto"
            style={{ background: "#00553E" }}
          >
            {submitting ? "Enregistrement…" : step === 3 ? "Valider" : "Suivant ➜"}
          </button>
        </div>
      </form>

      {soinModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setSoinModalOpen(false)}>
          <div className="w-full max-w-sm rounded-[12px] bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 text-lg font-bold text-[#0B1F16]">Ajouter un soin</div>
            <input
              ref={soinInputRef}
              value={soinDraft}
              onChange={(e) => setSoinDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); const v = soinDraft.trim(); if (v) { setSoins((prev) => [...prev, v]); setSoinModalOpen(false); } }
                if (e.key === "Escape") setSoinModalOpen(false);
              }}
              placeholder="Ex : radiologie, kinésithérapie…"
              className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 focus:outline-none"
              style={{ borderColor: "#E5E7EB" }}
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-3">
              <button type="button" onClick={() => setSoinModalOpen(false)} className="h-9 px-4 rounded-[10px] border text-sm font-medium text-[#0B1F16]" style={{ borderColor: "#E5E7EB" }}>Annuler</button>
              <button type="button" disabled={!soinDraft.trim()} onClick={() => { const v = soinDraft.trim(); if (v) { setSoins((prev) => [...prev, v]); setSoinModalOpen(false); } }} className="h-9 px-4 rounded-[10px] text-sm font-semibold text-white disabled:opacity-50" style={{ background: "#00553E" }}>Ajouter</button>
            </div>
          </div>
        </div>
      )}

      <PatientAccessOtpModal
        open={pendingDossier !== null}
        patientNip={pendingDossier?.patientNip ?? ""}
        patientDisplayName={[prenom, nom].filter(Boolean).join(" ") || undefined}
        onClose={() => {
          setPendingDossier(null);
          navigate("/etablissement/dashboard/patients");
        }}
        onSuccess={() => void handleDossierAccessGranted()}
      />
    </div>
  );
}

type FoundPatient = {
  nip: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  profession: string;
  dateNaissance: string;
};

function normalizeFoundPatient(raw: unknown): FoundPatient | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const nip = String(o.nip ?? "").trim();
  if (!/^\d{10}$/.test(nip)) return null;
  const rawBirth = String(o.dateNaissance ?? o.date_naissance ?? "");
  const dateNaissance = rawBirth ? (rawBirth.includes("T") ? rawBirth : `${rawBirth}T00:00:00.000Z`) : "";
  return {
    nip,
    nom: String(o.nom ?? ""),
    prenom: String(o.prenom ?? ""),
    email: String(o.email ?? ""),
    telephone: String(o.telephone ?? ""),
    adresse: String(o.adresse ?? ""),
    profession: String(o.profession ?? ""),
    dateNaissance,
  };
}

/**
 * `existe: false` = compte patient déjà créé (NIP trouvé via recherche) mais aucun dossier
 * médical rempli — c'est le seul cas où l'établissement peut soumettre `POST /dossiers-medicaux`
 * (le backend interdit toute modification d'`informationsMedicales` une fois le dossier créé).
 */
type DossierState = "idle" | "loading" | "needs-otp" | "empty" | "filled" | "error";

function ExistingPatientFlow({ onPatientNotFound }: { onPatientNotFound: (nip: string) => void }) {
  const navigate = useNavigate();
  const { establishment } = useAuth();
  const etablissementId = establishment?.id?.trim();

  const [nip, setNip] = useState("");
  const nipParts = useMemo(
    () => [nip.slice(0, 2), nip.slice(2, 4), nip.slice(4, 6), nip.slice(6, 8), nip.slice(8, 10)],
    [nip]
  );
  const nipPartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const nipValid = /^\d{10}$/.test(nip);

  const updateNipPart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...nipParts];
    parts[index] = digits;
    setNip(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) requestAnimationFrame(() => nipPartRefs.current[index + 1]?.focus());
  };
  const handleNipPartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = nipPartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try { prev.setSelectionRange(prev.value.length, prev.value.length); } catch { /* ignore */ }
      });
    }
  };
  const handleNipPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNip(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => nipPartRefs.current[nextIndex]?.focus());
  };

  const [searching, setSearching] = useState(false);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [foundPatient, setFoundPatient] = useState<FoundPatient | null>(null);

  const [dossierState, setDossierState] = useState<DossierState>("idle");
  const [dossierError, setDossierError] = useState<string | null>(null);
  const [existingDossier, setExistingDossier] = useState<{ numeroDossier: string; statut: string } | null>(null);

  const [taille, setTaille] = useState("");
  const [poids, setPoids] = useState("");
  const [gs, setGs] = useState("");
  const [electro, setElectro] = useState("");
  const [allergies, setAllergies] = useState("");
  const [intolerances, setIntolerances] = useState("");
  const [vaccins, setVaccins] = useState<string[]>(["Polio", "Fievre jaune", "COVID-19"]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const tailleRef = useRef<HTMLInputElement | null>(null);
  const poidsRef = useRef<HTMLInputElement | null>(null);
  const applyDigitsWithCaret = (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string) => void,
    ref: React.RefObject<HTMLInputElement | null>,
    maxLength: number
  ) => {
    const input = event.target;
    const selection = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = (input.value.slice(0, selection).match(/\d/g) || []).length;
    const only = input.value.replace(/\D/g, "").slice(0, maxLength);
    setter(only);
    requestAnimationFrame(() => {
      if (ref.current) {
        const position = Math.min(digitsBeforeCaret, only.length);
        try {
          ref.current.focus();
          ref.current.setSelectionRange(position, position);
        } catch {
          /* ignore */
        }
      }
    });
  };

  const loadDossier = async (patientNip: string) => {
    setDossierState("loading");
    setDossierError(null);
    const res = await getPatientMedicalFilePro(patientNip);
    if (!res.ok) {
      if (res.errorCode === "ACCES_DOSSIER_REQUIS") {
        setDossierState("needs-otp");
        return;
      }
      setDossierState("error");
      setDossierError(res.message);
      return;
    }
    const data = res.data && typeof res.data === "object" ? (res.data as Record<string, unknown>) : {};
    if (data.existe === false) {
      setDossierState("empty");
      return;
    }
    setDossierState("filled");
    setExistingDossier({
      numeroDossier: String(data.numeroDossier ?? ""),
      statut: String(data.statut ?? ""),
    });
  };

  const handleSearch = async () => {
    if (!nipValid || searching) return;
    setSearching(true);
    setSearchError(null);
    setSearchNotFound(false);
    setFoundPatient(null);
    setDossierState("idle");
    const res = await searchPatients({ nip });
    setSearching(false);
    if (!res.ok) {
      setSearchError(res.message);
      return;
    }
    const patient = normalizeFoundPatient(res.data[0]);
    if (!patient) {
      setSearchNotFound(true);
      return;
    }
    setFoundPatient(patient);
    await loadDossier(patient.nip);
  };

  const onSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSearch();
  };

  const handleSubmitDossier = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!foundPatient || !etablissementId || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    const poidsN = poids ? Number(poids) : undefined;
    const tailleN = taille ? Number(taille) : undefined;
    const allergiesList = allergies.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    const intolerancesList = intolerances.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    const vaccinationsPayload = vaccins.map((v) => ({
      type: v.toLowerCase().replace(/\s+/g, "_"),
      date: new Date().toISOString(),
    }));
    const dossierPayload: Record<string, unknown> = {
      patientNip: foundPatient.nip,
      informationsPersonnelles: {
        nom: foundPatient.nom,
        prenom: foundPatient.prenom,
        dateNaissance: foundPatient.dateNaissance,
        adresse: foundPatient.adresse,
        email: foundPatient.email,
        telephone: foundPatient.telephone,
        profession: foundPatient.profession || undefined,
      },
      informationsMedicales: {
        poids: poidsN,
        taille: tailleN,
        groupeSanguin: gs || undefined,
        electrophorese: electro || undefined,
        allergies: allergiesList,
        intolerances: intolerancesList,
        vaccinations: vaccinationsPayload,
        antecedents: [] as string[],
      },
      createur: etablissementId,
    };
    const res = await createMedicalRecord(dossierPayload);
    if (!res.ok) {
      setSubmitting(false);
      if (res.errorCode === "ACCES_DOSSIER_REQUIS") {
        setDossierState("needs-otp");
        return;
      }
      setSubmitError(res.message);
      return;
    }
    setSubmitting(false);
    navigate(`/etablissement/dashboard/patients/${encodeURIComponent(foundPatient.nip)}`);
  };

  return (
    <div>
      <div className="bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
        <div className="text-2xl font-bold text-[#0B1F16] mb-4">Rechercher un patient par NIP</div>
        <form onSubmit={onSearchSubmit} className="flex flex-wrap items-end gap-3">
          <div>
            <div className="font-semibold mb-1">
              NIP <RequiredMark />
            </div>
            <div className="flex items-center gap-2">
              {nipParts.map((part, index) => (
                <div key={`nip-part-${index}`} className="flex items-center gap-2">
                  <input
                    value={part}
                    ref={(el) => { nipPartRefs.current[index] = el; }}
                    onChange={(e) => updateNipPart(index, e.target.value)}
                    onKeyDown={(e) => handleNipPartKeyDown(index, e)}
                    onPaste={handleNipPaste}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={2}
                    placeholder="00"
                    className="h-10 w-12 text-center rounded-[10px] border px-1 bg-[#F3F4F4]"
                    style={{ borderColor: "#E5E7EB" }}
                  />
                  {index < 4 && <span className="text-[#0B1F16]/70 font-semibold">-</span>}
                </div>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={!nipValid || searching}
            className="h-10 px-4 rounded-[10px] font-semibold text-white disabled:opacity-60 inline-flex items-center gap-2"
            style={{ background: "#00553E" }}
          >
            <Search size={16} />
            {searching ? "Recherche…" : "Rechercher"}
          </button>
        </form>

        {searchError && (
          <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            {searchError}
          </div>
        )}

        {searchNotFound && (
          <div className="mt-4 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex flex-wrap items-center justify-between gap-3">
            <span>Aucun patient trouvé avec le NIP {nip}. Ce patient n'a peut-être pas encore de compte.</span>
            <button
              type="button"
              onClick={() => onPatientNotFound(nip)}
              className="h-9 px-4 rounded-[8px] font-semibold text-white shrink-0"
              style={{ background: "#00553E" }}
            >
              Créer un nouveau patient
            </button>
          </div>
        )}
      </div>

      {foundPatient && (
        <div className="mt-6 bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
          <div className="text-lg font-bold text-[#0B1F16] mb-3">
            {foundPatient.nom} {foundPatient.prenom}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#0B1F16]/80 mb-4">
            <div>NIP : <span className="font-mono">{foundPatient.nip}</span></div>
            <div>E-mail : {foundPatient.email || "—"}</div>
            <div>Téléphone : {foundPatient.telephone || "—"}</div>
            <div>Adresse : {foundPatient.adresse || "—"}</div>
          </div>

          {dossierState === "loading" && (
            <p className="text-sm text-[#0B1F16]/60">Vérification de l'accès au dossier…</p>
          )}

          {dossierState === "error" && (
            <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {dossierError}
            </div>
          )}

          {dossierState === "needs-otp" && (
            <p className="text-sm text-amber-700">En attente d'autorisation d'accès du patient…</p>
          )}

          {dossierState === "filled" && (
            <div className="rounded-[10px] border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex flex-wrap items-center justify-between gap-3">
              <span>
                Ce patient a déjà un dossier médical rempli
                {existingDossier?.numeroDossier ? ` (${existingDossier.numeroDossier})` : ""}.
              </span>
              <button
                type="button"
                onClick={() => navigate(`/etablissement/dashboard/patients/${encodeURIComponent(foundPatient.nip)}`)}
                className="h-9 px-4 rounded-[8px] font-semibold text-white shrink-0"
                style={{ background: "#00553E" }}
              >
                Voir le dossier
              </button>
            </div>
          )}

          {dossierState === "empty" && (
            <form onSubmit={handleSubmitDossier} className="mt-2 space-y-4">
              {submitError && (
                <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  {submitError}
                </div>
              )}
              <div className="text-lg font-bold text-[#0B1F16]">Informations médicales</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-semibold mb-1">Taille</div>
                  <div className="relative">
                    <Input
                      value={taille}
                      onChange={(e) => applyDigitsWithCaret(e, setTaille, tailleRef, 4)}
                      ref={tailleRef}
                      inputMode="numeric"
                      pattern="\d*"
                      className="pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">cm</span>
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-1">Groupe sanguin</div>
                  <Select value={gs || undefined} onValueChange={setGs}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {GROUPES_SANGUINS.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="font-semibold mb-1">Poids</div>
                  <div className="relative">
                    <Input
                      value={poids}
                      onChange={(e) => applyDigitsWithCaret(e, setPoids, poidsRef, 4)}
                      ref={poidsRef}
                      inputMode="numeric"
                      pattern="\d*"
                      className="pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kg</span>
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-1">Electrophorèse</div>
                  <Select value={electro || undefined} onValueChange={setElectro}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {ELECTROPHORESES.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="font-semibold mb-1">Allergies</div>
                  <Input value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Ex : pénicilline, arachide…" />
                </div>
                <div>
                  <div className="font-semibold mb-1">Intolérances</div>
                  <Input value={intolerances} onChange={(e) => setIntolerances(e.target.value)} placeholder="Ex : lactose, gluten…" />
                </div>
                <div className="md:col-span-2">
                  <div className="font-semibold mb-2">Vaccinations</div>
                  <div className="flex flex-wrap gap-2 relative">
                    {vaccins.map((v) => (
                      <span key={v} className="inline-flex items-center gap-2 px-3 h-9 rounded-[10px] border bg-white" style={{ borderColor: "#E5E7EB" }}>
                        {v}
                        <button type="button" onClick={() => setVaccins((prev) => prev.filter((x) => x !== v))}>✕</button>
                      </span>
                    ))}
                    <div className="relative">
                      <button type="button" className="inline-flex items-center px-3 h-9 rounded-[10px] border bg-white" style={{ borderColor: "#E5E7EB" }} onClick={() => setPickerOpen((o) => !o)}>
                        Ajouter
                      </button>
                      {pickerOpen && (
                        <div className="absolute z-10 mt-2 w-56 bg-white rounded-[10px] border p-3 space-y-2 shadow" style={{ borderColor: "#E5E7EB" }}>
                          {["Varicelle", "BCG", "DTP", "MMR", "Hépatite", "HPV", "Hib"].map((opt) => (
                            <label key={opt} className="flex items-center gap-2 text-sm">
                              <input type="checkbox" checked={vaccins.includes(opt)} onChange={(e) => setVaccins((prev) => e.target.checked ? [...prev, opt] : prev.filter((x) => x !== opt))} />
                              {opt}
                            </label>
                          ))}
                          <div className="pt-2 flex justify-end">
                            <button type="button" className="h-9 px-3 rounded-[10px] font-semibold text-white" style={{ background: "#00553E" }} onClick={() => setPickerOpen(false)}>Ajouter</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-11 px-5 rounded-[10px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                >
                  {submitting ? "Enregistrement…" : "Créer le dossier médical"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <PatientAccessOtpModal
        open={dossierState === "needs-otp"}
        patientNip={foundPatient?.nip ?? ""}
        patientDisplayName={foundPatient ? [foundPatient.prenom, foundPatient.nom].filter(Boolean).join(" ") || undefined : undefined}
        onClose={() => setDossierState("idle")}
        onSuccess={() => {
          if (foundPatient) void loadDossier(foundPatient.nip);
        }}
      />
    </div>
  );
}
